import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import Loader from '../shared/Loader';
import SEO from '../shared/SEO';

const PRODUCT_TYPES = {
  cuadro: { label: 'Cuadro', icon: 'fa-image' },
  remera: { label: 'Remera', icon: 'fa-shirt' },
  almohadon: { label: 'Almohadón', icon: 'fa-couch' },
  otro: { label: 'Otro', icon: 'fa-box' },
};

const CustomizableProductList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const res = await fetch(API_ENDPOINTS.customizableProducts.getAll());
      const data = await res.json();
      setProducts(data);
    } catch (error) {
      console.error('Error loading customizable products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = filterType
    ? products.filter(p => p.productType === filterType)
    : products;

  if (loading) return <Loader />;

  return (
    <>
      <SEO
        title="Personalizados IA - Creá con tu Foto"
        description="Personalizá cuadros, remeras y almohadones con tu propia foto. Subí tu imagen y recibí tu producto único en casa."
        url="/personalizados/ia"
      />

      <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
        {/* Hero */}
        <div style={{
          background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #1f8e38 100%)',
          color: '#fff', padding: '60px 20px', textAlign: 'center'
        }}>
          <div className="container">
            <h1 style={{ fontSize: '2.5rem', fontWeight: '800', marginBottom: '12px' }}>
              <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '12px' }}></i>
              Personalizados IA
            </h1>
            <p style={{ fontSize: '1.15rem', opacity: 0.85, maxWidth: '650px', margin: '0 auto' }}>
              Subí tu foto y la imprimimos en el producto que elijas. Cuadros, remeras y almohadones únicos.
            </p>
            <p style={{ fontSize: '0.95rem', opacity: 0.65, maxWidth: '500px', margin: '10px auto 0' }}>
              ¿No tenés una imagen? Generá diseños increíbles con nuestro estudio de IA.
            </p>
          </div>
        </div>

        <div className="container" style={{ padding: '40px 20px' }}>
          {/* Filtros por tipo */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginBottom: '30px', flexWrap: 'wrap' }}>
            <button
              className={`btn ${!filterType ? 'btn-dark' : 'btn-outline-dark'}`}
              onClick={() => setFilterType('')}
            >
              Todos
            </button>
            {Object.entries(PRODUCT_TYPES).map(([key, val]) => (
              <button
                key={key}
                className={`btn ${filterType === key ? 'btn-dark' : 'btn-outline-dark'}`}
                onClick={() => setFilterType(key)}
              >
                <i className={`fas ${val.icon}`} style={{ marginRight: '6px' }}></i>
                {val.label}
              </button>
            ))}
          </div>

          {/* Grid de productos */}
          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
              <i className="fas fa-wand-magic-sparkles" style={{ fontSize: '3rem', marginBottom: '15px', display: 'block' }}></i>
              <h5>No hay productos disponibles en esta categoría</h5>
            </div>
          ) : (
            <div className="row">
              {filtered.map(product => {
                const typeInfo = PRODUCT_TYPES[product.productType] || PRODUCT_TYPES.otro;
                return (
                  <div key={product._id} className="col-6 col-md-4 col-lg-3 mb-4">
                    <Link to={`/personalizado/${product._id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                      <div style={{
                        background: '#fff', borderRadius: '16px', overflow: 'hidden',
                        border: '1px solid #eee', transition: 'all 0.3s',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                      }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.06)';
                        }}
                      >
                        {/* Imagen */}
                        <div style={{ position: 'relative', paddingTop: '100%', background: '#f0f0f0' }}>
                          {product.images && product.images[0] ? (
                            <img
                              src={getDisplayImageUrl(product.images[0])}
                              alt={product.name}
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            <div style={{
                              position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb'
                            }}>
                              <i className={`fas ${typeInfo.icon}`} style={{ fontSize: '3rem' }}></i>
                            </div>
                          )}
                          <span style={{
                            position: 'absolute', top: '10px', left: '10px',
                            background: '#1f8e38', color: '#fff', padding: '4px 12px',
                            borderRadius: '20px', fontSize: '0.75rem', fontWeight: '600'
                          }}>
                            <i className={`fas ${typeInfo.icon}`} style={{ marginRight: '4px' }}></i>
                            {typeInfo.label}
                          </span>
                        </div>

                        {/* Info */}
                        <div style={{ padding: '16px' }}>
                          <h6 style={{ fontWeight: '700', margin: '0 0 6px', fontSize: '1rem' }}>{product.name}</h6>
                          <p style={{
                            color: '#888', fontSize: '0.85rem', margin: '0 0 10px',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {product.description}
                          </p>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1f8e38' }}>
                              ${product.basePrice?.toLocaleString()}
                            </span>
                            {product.sizes && product.sizes.length > 0 && (
                              <span style={{ fontSize: '0.75rem', color: '#999' }}>
                                {product.sizes.length} tamaño(s)
                              </span>
                            )}
                          </div>
                          <button style={{
                            width: '100%', marginTop: '12px', padding: '10px',
                            background: '#1f8e38', color: '#fff', border: 'none',
                            borderRadius: '10px', fontWeight: '600', fontSize: '0.9rem',
                            cursor: 'pointer'
                          }}>
                            <i className="fas fa-wand-magic-sparkles" style={{ marginRight: '6px' }}></i>
                            Personalizar
                          </button>
                        </div>
                      </div>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sección promocional: Estudio IA */}
          <div style={{
            marginTop: '60px',
            borderRadius: '20px',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #000 0%, #1a1a1a 50%, #1f8e38 100%)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            position: 'relative',
          }}>
            {/* Decorative elements */}
            <div style={{
              position: 'absolute', top: '-40px', right: '-40px',
              width: '200px', height: '200px',
              background: 'radial-gradient(circle, rgba(31,142,56,0.25) 0%, transparent 70%)',
              borderRadius: '50%', pointerEvents: 'none'
            }} />
            <div style={{
              position: 'absolute', bottom: '-30px', left: '-30px',
              width: '160px', height: '160px',
              background: 'radial-gradient(circle, rgba(31,142,56,0.15) 0%, transparent 70%)',
              borderRadius: '50%', pointerEvents: 'none'
            }} />

            <div style={{ padding: '50px 40px', position: 'relative', zIndex: 1 }}>
              <div className="row align-items-center">
                <div className="col-lg-7 mb-4 mb-lg-0">
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    background: 'rgba(31,142,56,0.15)', border: '1px solid rgba(31,142,56,0.3)',
                    borderRadius: '25px', padding: '6px 16px', marginBottom: '20px'
                  }}>
                    <i className="fas fa-sparkles" style={{ color: '#4ade80', fontSize: '0.85rem' }}></i>
                    <span style={{ color: '#4ade80', fontSize: '0.85rem', fontWeight: '600' }}>Nuevo</span>
                  </div>

                  <h2 style={{
                    color: '#fff', fontWeight: '800', fontSize: '2rem',
                    lineHeight: '1.2', marginBottom: '16px'
                  }}>
                    Generá imágenes únicas con{' '}
                    <span style={{ color: '#4ade80' }}>
                      Inteligencia Artificial
                    </span>
                  </h2>

                  <p style={{
                    color: 'rgba(255,255,255,0.7)', fontSize: '1.05rem',
                    lineHeight: '1.7', marginBottom: '24px', maxWidth: '500px'
                  }}>
                    Nuestro estudio de IA te permite crear diseños originales a partir de texto.
                    Imaginá lo que quieras, la IA lo genera, y después podés imprimirlo en cualquier producto personalizado.
                  </p>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
                    {[
                      { icon: 'fa-pen-fancy', text: 'Describí tu idea en texto y la IA la convierte en imagen' },
                      { icon: 'fa-palette', text: 'Múltiples estilos: arte digital, realista, ilustración y más' },
                      { icon: 'fa-print', text: 'Usá tu imagen generada para personalizar cuadros, remeras, etc.' },
                    ].map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '10px',
                          background: 'rgba(31,142,56,0.15)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', flexShrink: 0
                        }}>
                          <i className={`fas ${item.icon}`} style={{ color: '#4ade80', fontSize: '0.85rem' }}></i>
                        </div>
                        <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>{item.text}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href="https://ai.rementerprises.uy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '10px',
                      background: 'linear-gradient(135deg, #1f8e38 0%, #16a34a 100%)',
                      color: '#fff', padding: '14px 32px', borderRadius: '14px',
                      fontSize: '1.05rem', fontWeight: '700', textDecoration: 'none',
                      boxShadow: '0 4px 20px rgba(31,142,56,0.4)',
                      transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(31,142,56,0.5)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(31,142,56,0.4)';
                    }}
                  >
                    <i className="fas fa-robot"></i>
                    Probar el Estudio IA
                    <i className="fas fa-arrow-right" style={{ fontSize: '0.85rem' }}></i>
                  </a>
                </div>

                <div className="col-lg-5">
                  <div style={{
                    background: 'rgba(255,255,255,0.05)', borderRadius: '20px',
                    border: '1px solid rgba(255,255,255,0.1)', padding: '30px',
                    backdropFilter: 'blur(10px)',
                  }}>
                    {/* Mockup de prompt */}
                    <div style={{
                      background: 'rgba(0,0,0,0.3)', borderRadius: '12px',
                      padding: '16px', marginBottom: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ff5f57' }} />
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ffbd2e' }} />
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#28c840' }} />
                        <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem', marginLeft: '8px' }}>ai.rementerprises.uy</span>
                      </div>
                      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', margin: '0 0 8px', fontStyle: 'italic' }}>
                        "Un paisaje de montañas al atardecer con colores vibrantes estilo pintura al óleo"
                      </p>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        color: '#4ade80', fontSize: '0.8rem'
                      }}>
                        <i className="fas fa-wand-magic-sparkles"></i>
                        <span>Generando imagen...</span>
                      </div>
                    </div>

                    {/* Resultado simulado */}
                    <div style={{
                      background: 'linear-gradient(135deg, #1a1a1a 0%, #1f8e38 50%, #4ade80 100%)',
                      borderRadius: '12px', paddingTop: '75%', position: 'relative',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        color: '#fff',
                      }}>
                        <i className="fas fa-image" style={{ fontSize: '2.5rem', marginBottom: '10px', opacity: 0.8 }}></i>
                        <span style={{ fontWeight: '700', fontSize: '0.95rem' }}>Tu imagen generada</span>
                        <span style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '4px' }}>lista para personalizar</span>
                      </div>
                    </div>

                    <div style={{
                      display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center'
                    }}>
                      {['fa-image', 'fa-shirt', 'fa-couch'].map((icon, i) => (
                        <div key={i} style={{
                          width: '44px', height: '44px', borderRadius: '12px',
                          background: 'rgba(31,142,56,0.15)', border: '1px solid rgba(31,142,56,0.3)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                          <i className={`fas ${icon}`} style={{ color: '#4ade80', fontSize: '0.9rem' }}></i>
                        </div>
                      ))}
                    </div>
                    <p style={{
                      textAlign: 'center', color: 'rgba(255,255,255,0.5)',
                      fontSize: '0.75rem', marginTop: '8px', marginBottom: 0
                    }}>
                      Imprimí en cuadros, remeras o almohadones
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomizableProductList;
